import { ChangeEvent, DragEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { queryClient } from '../../../app/query-client';
import { getRuntimeConfig } from '../../../app/config/runtime-config';
import { queryKeys } from '../../../api/query-keys';
import { uploadPresignedFile } from '../../../api/upload-client';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { pinInvestigationEvidence, respondToInvestigationInquiry } from '../../investigations/api/investigations.api';
import { useMachines } from '../../machines/api/useMachines';
import { cancelDocumentUpload, createUploadRequests } from '../api/documents.api';
import { uploadLimitGuidance, validateSelectedFile } from '../document-upload-policy';
import { canonicalContentType, formatFileSize, isImageFileType } from '../types/document';

type LocalUploadState = 'selected' | 'authorizing' | 'uploading' | 'complete' | 'rejected' | 'failed';

type LocalUpload = {
  clientId: string;
  file: File;
  state: LocalUploadState;
  progress: number;
  documentId?: string;
  uploadUrl?: string;
  uploadExpiresAt?: string;
  requiredHeaders?: Record<string, string>;
  error?: string;
};

const acceptedExtensions = '.pdf,.docx,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp';

export function AddDocumentsPage() {
  const { machineId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const uploadLimits = getRuntimeConfig().documentUploadLimitsBytes;
  const machines = useMachines();
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllers = useRef(new Map<string, AbortController>());
  const linkedInquiryDocuments = useRef(new Set<string>());
  const [items, setItems] = useState<LocalUpload[]>([]);
  const [dragging, setDragging] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [selectionGuidance, setSelectionGuidance] = useState<string | null>(null);
  const machine = machines.data?.machines.find((item) => item.id === machineId);
  const firstMachineOnboarding = searchParams.get('onboarding') === 'first-machine';
  const investigationId = searchParams.get('investigationId')?.trim() || '';
  const inquiryId = searchParams.get('inquiryId')?.trim() || '';
  const rawReturnTo = searchParams.get('returnTo')?.trim() || '';
  const returnTarget = rawReturnTo.startsWith(`/machines/${machineId}/`) ? rawReturnTo : `/machines/${machineId}/documents`;
  const inquiryEvidenceMode = Boolean(investigationId && inquiryId);
  const busy = items.some((item) => item.state === 'authorizing' || item.state === 'uploading');
  const completeCount = items.filter((item) => item.state === 'complete').length;
  const imageCount = items.filter((item) => isImageUpload(item.file)).length;
  const completeImageCount = items.filter((item) => item.state === 'complete' && isImageUpload(item.file)).length;
  const completeDocumentCount = completeCount - completeImageCount;
  const failedCount = items.filter((item) => item.state === 'failed' || item.state === 'rejected').length;
  const readyToStart = items.filter((item) => item.state === 'selected');
  const failedWithAuthorization = items.filter((item) => item.state === 'failed' && item.uploadUrl && item.documentId && !authorizationExpired(item));
  const pendingServerRecords = items.filter((item) => item.documentId && item.state !== 'complete');
  const totalSize = useMemo(() => items.reduce((sum, item) => sum + item.file.size, 0), [items]);

  useEffect(() => () => {
    abortControllers.current.forEach((controller) => controller.abort());
    abortControllers.current.clear();
  }, []);

  if (!auth.can(permissions.documentsUpload)) return <Navigate to={`/machines/${machineId}/documents`} replace />;

  function addFiles(files: File[]) {
    setBatchError(null);
    const next = [...items];
    const rejectedMessages: string[] = [];
    for (const file of files) {
      if (next.length >= 50) {
        rejectedMessages.push('Route42 accepts up to 50 files in one upload batch. Upload these files first, then start another batch.');
        break;
      }
      const duplicate = next.some((item) => item.file.name === file.name && item.file.size === file.size && item.file.lastModified === file.lastModified);
      if (duplicate) continue;
      const policyError = validateSelectedFile(file, uploadLimits);
      if (policyError) {
        rejectedMessages.push(policyError);
        continue;
      }
      next.push({ clientId: newClientFileId(), file, state: 'selected', progress: 0 });
    }
    setItems(next);
    setSelectionGuidance(rejectedMessages.length ? Array.from(new Set(rejectedMessages)).join(' ') : null);
  }

  function onFileInput(event: ChangeEvent<HTMLInputElement>) {
    addFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    addFiles(Array.from(event.dataTransfer.files));
  }

  async function beginUpload() {
    const selected = items.filter((item) => item.state === 'selected');
    if (!selected.length) return;
    setBatchError(null);
    setSelectionGuidance(null);
    updateMany(selected.map((item) => item.clientId), { state: 'authorizing', progress: 0, error: undefined });

    try {
      const authorization = await createUploadRequests(
        machineId,
        selected.map((item) => ({ clientFileId: item.clientId, file: item.file })),
      );
      const byClient = new Map(authorization.uploads.map((upload) => [upload.client_file_id, upload]));
      const accepted = selected.flatMap((local) => {
        const result = byClient.get(local.clientId);
        return result && result.status === 'AWAITING_UPLOAD' && result.upload_url && result.document_id
          ? [{ local, authorization: result }]
          : [];
      });

      setItems((current) => current.map((item) => {
        if (!selected.some((selectedItem) => selectedItem.clientId === item.clientId)) return item;
        const result = byClient.get(item.clientId);
        if (!result || result.status !== 'AWAITING_UPLOAD' || !result.upload_url || !result.document_id) {
          return { ...item, state: 'rejected', error: result?.message ?? 'Route42 could not authorize this file for upload.' };
        }
        return {
          ...item,
          state: 'uploading',
          documentId: result.document_id,
          uploadUrl: result.upload_url,
          uploadExpiresAt: result.expires_at,
          requiredHeaders: result.required_headers ?? { 'Content-Type': canonicalContentType(item.file) },
          error: undefined,
        };
      }));

      await uploadWithConcurrency(accepted, 3, async ({ local, authorization: result }) => {
        if (!result.upload_url || !result.document_id) return;
        await uploadOne({
          clientId: local.clientId,
          file: local.file,
          documentId: result.document_id,
          uploadUrl: result.upload_url,
          requiredHeaders: result.required_headers ?? { 'Content-Type': canonicalContentType(local.file) },
        });
      });
      await refreshDocuments();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Route42 could not prepare these files for upload.';
      setBatchError(message);
      updateMany(selected.map((item) => item.clientId), { state: 'selected' });
    }
  }

  async function retryFailed() {
    setBatchError(null);
    const retryable = items.filter((item) => item.state === 'failed' && item.uploadUrl && item.documentId && !authorizationExpired(item));
    await uploadWithConcurrency(retryable, 3, async (item) => {
      if (!item.uploadUrl || !item.documentId) return;
      await uploadOne({ clientId: item.clientId, file: item.file, documentId: item.documentId, uploadUrl: item.uploadUrl, requiredHeaders: item.requiredHeaders });
    });
    await refreshDocuments();
  }

  async function uploadOne(input: { clientId: string; file: File; documentId: string; uploadUrl: string; requiredHeaders?: Record<string, string> }) {
    const controller = new AbortController();
    abortControllers.current.set(input.clientId, controller);
    updateItem(input.clientId, { state: 'uploading', progress: 0, error: undefined });
    try {
      await uploadPresignedFile({
        url: input.uploadUrl,
        file: input.file,
        documentId: input.documentId,
        requiredHeaders: input.requiredHeaders,
        signal: controller.signal,
        onProgress: ({ percent }) => updateItem(input.clientId, { progress: percent }),
      });
      if (!controller.signal.aborted) {
        updateItem(input.clientId, { state: 'complete', progress: 100 });
        if (inquiryEvidenceMode && !linkedInquiryDocuments.current.has(input.documentId)) {
          try {
            await pinInvestigationEvidence(investigationId, 'DOCUMENT', input.documentId, `Added for Active Reasoning inquiry ${inquiryId}`);
            await respondToInvestigationInquiry(investigationId, inquiryId, 'EVIDENCE_ADDED', `Added ${input.file.name} as evidence for this inquiry.`);
            linkedInquiryDocuments.current.add(input.documentId);
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId) }),
              queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoning(auth.activeCustomerId, investigationId) }),
              queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningFreshness(auth.activeCustomerId, investigationId) }),
              queryClient.invalidateQueries({ queryKey: queryKeys.investigationInquiries(auth.activeCustomerId, investigationId) }),
              queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningHistory(auth.activeCustomerId, investigationId) }),
            ]);
          } catch (error) {
            setBatchError(error instanceof Error ? error.message : 'The upload completed, but Route42 could not associate it with the Investigation inquiry.');
          }
        }
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        updateItem(input.clientId, { state: 'failed', error: error instanceof Error ? error.message : 'Upload failed.' });
      }
    } finally {
      abortControllers.current.delete(input.clientId);
    }
  }

  async function cancelOne(item: LocalUpload, reason: 'USER_CANCELLED' | 'USER_REMOVED' | 'USER_RETRY' = 'USER_CANCELLED') {
    abortControllers.current.get(item.clientId)?.abort();
    if (!item.documentId) {
      removeItem(item.clientId);
      return;
    }
    try {
      await cancelDocumentUpload(item.documentId, reason);
      removeItem(item.clientId);
      await refreshDocuments();
    } catch (error) {
      setBatchError(error instanceof Error ? error.message : 'Route42 could not cancel this upload.');
      await refreshDocuments();
    }
  }

  async function cancelAllAndLeave() {
    setBatchError(null);
    const cancellable = items.filter((item) => (item.state === 'uploading' || item.state === 'authorizing' || item.state === 'failed') && item.documentId);
    abortControllers.current.forEach((controller) => controller.abort());
    await Promise.allSettled(cancellable.map((item) => cancelDocumentUpload(item.documentId!, 'USER_CANCELLED')));
    await refreshDocuments();
    navigate(returnTarget);
  }

  async function refreshDocuments() {
    await queryClient.invalidateQueries({ queryKey: queryKeys.documents(auth.activeCustomerId, machineId) });
  }

  function updateItem(clientId: string, patch: Partial<LocalUpload>) {
    setItems((current) => current.map((item) => item.clientId === clientId ? { ...item, ...patch } : item));
  }

  function updateMany(clientIds: string[], patch: Partial<LocalUpload>) {
    const ids = new Set(clientIds);
    setItems((current) => current.map((item) => ids.has(item.clientId) ? { ...item, ...patch } : item));
  }

  function removeItem(clientId: string) {
    setItems((current) => current.filter((item) => item.clientId !== clientId));
  }

  return (
    <section className="r42-page r42-page--wide r42-add-documents-page">
      <div className="r42-add-documents-head">
        <Link className="r42-back-link" to={returnTarget}><i className="bi bi-arrow-left" aria-hidden="true" /> {inquiryEvidenceMode ? 'Investigation' : 'Documents'}</Link>
        <span className="r42-eyebrow">Add machine information</span>
        <h1>Add documents or images</h1>
        <p>{machine?.name ? `Give Route42 trustworthy, machine-specific evidence about ${machine.name}.` : 'Give Route42 trustworthy, machine-specific evidence it can learn from.'}</p>
      </div>

      {inquiryEvidenceMode ? <div className="r42-image-upload-guidance" role="status"><span className="r42-image-upload-guidance__icon"><i className="bi bi-compass" aria-hidden="true" /></span><div><strong>Adding evidence to the current Investigation inquiry.</strong><span>Completed uploads will be linked to the Investigation and Route42 will mark its reasoning as ready to refresh.</span></div></div> : null}

      {batchError ? <div className="r42-form-alert" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Route42 couldn't complete that action.</strong><span>{batchError}</span></div></div> : null}
      {selectionGuidance ? <div className="r42-form-alert r42-form-alert--warning" role="status"><i className="bi bi-info-circle" aria-hidden="true" /><div><strong>Some files were not added.</strong><span>{selectionGuidance}</span></div></div> : null}

      <div
        className={`r42-drop-zone${dragging ? ' is-dragging' : ''}`}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <input ref={inputRef} type="file" multiple accept={acceptedExtensions} onChange={onFileInput} hidden aria-label="Choose machine files" />
        <span className="r42-drop-zone__icon"><i className="bi bi-cloud-arrow-up" aria-hidden="true" /></span>
        <h2>Drop machine documents or images here</h2>
        <p>Use machine-specific files Route42 can ground as evidence: manuals and records, component photos, HMI/control-panel images, or nameplates and labels.</p>
        <div className="r42-upload-source-examples" aria-label="Examples of useful machine information">
          <span><i className="bi bi-file-earmark-text" aria-hidden="true" /> Manuals & records</span>
          <span><i className="bi bi-camera" aria-hidden="true" /> Machine photos</span>
          <span><i className="bi bi-display" aria-hidden="true" /> HMI / control panels</span>
          <span><i className="bi bi-upc-scan" aria-hidden="true" /> Nameplates / labels</span>
        </div>
        <p className="r42-drop-zone__limits"><strong>File limits:</strong> {uploadLimitGuidance(uploadLimits)}. Up to 50 files per batch.</p>
        <p className="r42-drop-zone__trust"><i className="bi bi-shield-check" aria-hidden="true" /> For images, Route42 records visible observations and readable text—not an image-only diagnosis.</p>
        <button type="button" className="btn btn-outline-secondary" onClick={() => inputRef.current?.click()} disabled={busy}>Choose files</button>
      </div>

      {imageCount > 0 ? (
        <div className="r42-image-upload-guidance" role="status">
          <span className="r42-image-upload-guidance__icon"><i className="bi bi-camera" aria-hidden="true" /></span>
          <div>
            <strong>{imageCount} {imageCount === 1 ? 'image is' : 'images are'} ready as image evidence.</strong>
            <span>Route42 will analyze visible conditions and readable text, then add only observations it can support. A photo may suggest a condition, but it does not by itself confirm root cause, failure mode, or a repair decision.</span>
          </div>
        </div>
      ) : null}

      {items.length ? (
        <section className="r42-upload-panel" aria-label="Selected files">
          <div className="r42-upload-panel__head">
            <div><strong>{items.length} {items.length === 1 ? 'file' : 'files'}</strong><span>{formatFileSize(totalSize)} total{imageCount ? ` · ${imageCount} image${imageCount === 1 ? '' : 's'}` : ''}</span></div>
            {!busy && completeCount === 0 ? <button className="r42-text-button" type="button" onClick={() => { setItems([]); setSelectionGuidance(null); }}>Clear all</button> : null}
          </div>
          <div className="r42-upload-list">
            {items.map((item) => (
              <div className="r42-upload-row" key={item.clientId}>
                <span className="r42-document-icon"><i className={isImageUpload(item.file) ? 'bi bi-file-earmark-image' : 'bi bi-file-earmark-text'} aria-hidden="true" /></span>
                <div className="r42-upload-row__body">
                  <strong>{item.file.name}</strong>
                  <span>{isImageUpload(item.file) ? 'Image evidence' : 'Document'} · {formatFileSize(item.file.size)} · {uploadStateLabel(item)}</span>
                  {(item.state === 'uploading' || item.state === 'complete') ? <div className="r42-upload-progress"><span style={{ width: `${item.progress}%` }} /></div> : null}
                  {item.error ? <small className="r42-upload-error">{item.error}</small> : null}
                </div>
                {item.state === 'selected' ? <button className="r42-icon-button" type="button" onClick={() => removeItem(item.clientId)} aria-label={`Remove ${item.file.name}`}><i className="bi bi-x-lg" aria-hidden="true" /></button> : null}
                {item.state === 'uploading' ? <button className="r42-icon-button" type="button" onClick={() => void cancelOne(item)} aria-label={`Cancel upload ${item.file.name}`} title="Cancel upload"><i className="bi bi-x-lg" aria-hidden="true" /></button> : null}
                {item.state === 'failed' ? <button className="r42-icon-button" type="button" onClick={() => void cancelOne(item, 'USER_REMOVED')} aria-label={`Remove interrupted upload ${item.file.name}`} title="Remove interrupted upload"><i className="bi bi-x-lg" aria-hidden="true" /></button> : null}
                {item.state === 'rejected' ? <button className="r42-icon-button" type="button" onClick={() => removeItem(item.clientId)} aria-label={`Remove rejected file ${item.file.name}`} title="Remove file"><i className="bi bi-x-lg" aria-hidden="true" /></button> : null}
                {item.state === 'complete' ? <i className="bi bi-check-circle-fill r42-upload-success" aria-label="Upload complete" /> : null}
                {item.state === 'rejected' || item.state === 'failed' ? <i className="bi bi-exclamation-circle-fill r42-upload-failed" aria-label="Upload interrupted" /> : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {completeCount > 0 && !busy ? (
        <div className={`r42-upload-complete${firstMachineOnboarding ? ' r42-upload-complete--onboarding' : ''}`} role="status">
          <i className="bi bi-check-circle" aria-hidden="true" />
          <div>
            <strong>{uploadCompleteHeading(completeCount, completeImageCount, completeDocumentCount)}</strong>
            <span>{firstMachineOnboarding ? 'Step 2 is complete. Route42 is learning from your source in the background while you move to the question workspace.' : uploadCompleteDescription(completeImageCount, completeDocumentCount)}</span>
          </div>
          <div className="r42-upload-complete__actions">
            {firstMachineOnboarding ? (
              <Link className="btn r42-btn-primary" to={`/machines/${machineId}/conversations/new?onboarding=first-machine`}>
                Continue to questions <i className="bi bi-arrow-right" aria-hidden="true" />
              </Link>
            ) : inquiryEvidenceMode ? <Link className="btn r42-btn-primary" to={returnTarget}>Return to Investigation <i className="bi bi-arrow-right" aria-hidden="true" /></Link> : null}
            <Link className="btn btn-sm btn-outline-secondary" to={`/machines/${machineId}/documents`}>View documents</Link>
          </div>
        </div>
      ) : null}

      <div className="r42-upload-actions">
        {busy ? (
          <button className="btn btn-outline-secondary" type="button" onClick={() => void cancelAllAndLeave()}>Cancel uploads</button>
        ) : pendingServerRecords.length ? (
          <button className="btn btn-outline-secondary" type="button" onClick={() => void cancelAllAndLeave()}>{completeCount ? 'Done' : 'Cancel'}</button>
        ) : (
          <Link className="btn btn-outline-secondary" to={returnTarget}>{completeCount ? (inquiryEvidenceMode ? 'Return to Investigation' : 'Done') : 'Cancel'}</Link>
        )}
        {failedWithAuthorization.length > 0 && !busy ? <button className="btn btn-outline-secondary" type="button" onClick={() => void retryFailed()}>Retry failed uploads</button> : null}
        {readyToStart.length > 0 ? (
          <button className="btn r42-btn-primary" type="button" onClick={() => void beginUpload()} disabled={busy}>
            {busy ? 'Uploading…' : `Upload ${readyToStart.length} ${readyToStart.length === 1 ? 'file' : 'files'}`}
          </button>
        ) : null}
      </div>

      {failedCount > 0 ? <p className="r42-upload-footnote">Interrupted or rejected files were not treated as machine knowledge. Retry while the secure upload is still valid, or remove them and select the source again.</p> : null}
    </section>
  );
}

function newClientFileId(): string {
  return typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `file-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function authorizationExpired(item: LocalUpload): boolean {
  if (!item.uploadExpiresAt) return false;
  const expires = new Date(item.uploadExpiresAt).valueOf();
  return Number.isFinite(expires) && Date.now() >= expires;
}

function uploadStateLabel(item: LocalUpload): string {
  if (item.state === 'selected') return 'Ready to upload';
  if (item.state === 'authorizing') return 'Preparing secure upload';
  if (item.state === 'uploading') return `Uploading ${item.progress}%`;
  if (item.state === 'complete') return isImageUpload(item.file) ? 'Upload complete — visible evidence analysis is starting' : 'Upload complete — Route42 is starting to learn';
  if (item.state === 'rejected') return 'Not accepted';
  if (authorizationExpired(item)) return 'Upload interrupted — secure upload expired';
  return 'Upload interrupted';
}

function isImageUpload(file: File): boolean {
  const extension = file.name.split('.').pop() ?? '';
  return isImageFileType(extension);
}

function uploadCompleteHeading(total: number, imageCount: number, documentCount: number): string {
  if (imageCount > 0 && documentCount === 0) return `${total} ${total === 1 ? 'image upload is' : 'image uploads are'} complete.`;
  if (documentCount > 0 && imageCount === 0) return `${total} ${total === 1 ? 'document upload is' : 'document uploads are'} complete.`;
  return `${total} uploads are complete.`;
}

function uploadCompleteDescription(imageCount: number, documentCount: number): string {
  if (imageCount > 0 && documentCount === 0) return 'Route42 is analyzing visible evidence and readable text in the background. You can leave this screen.';
  if (imageCount > 0 && documentCount > 0) return 'Route42 is analyzing visible evidence in images and learning from documents in the background. You can leave this screen.';
  return 'Route42 will continue learning from these documents in the background. You can leave this screen.';
}

async function uploadWithConcurrency<T>(items: T[], limit: number, worker: (item: T) => Promise<void>) {
  let next = 0;
  async function run() {
    while (next < items.length) {
      const index = next++;
      await worker(items[index]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => run()));
}
